import React from 'react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChartProps } from '../../types/visualization';
import CustomTooltip from '../common/CustomTooltip';

const DEFAULT_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4'];

const StackedBarChart: React.FC<ChartProps> = ({ 
  data, 
  config, 
  colors = DEFAULT_COLORS, 
  height = 350 
}) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  // Get chart configuration
  const xAxisColumn = config?.xAxis?.column || Object.keys(data[0])[0];

  // For stacked bar charts, we need to pivot the data by the grouping column
  // Auto-detect the grouping column (look for common patterns)
  const possibleGroupColumns = ['AccountType', 'InstitutionName', 'Category', 'Type', 'Status'];
  const groupColumn = possibleGroupColumns.find(col => 
    data.some(d => d[col] !== undefined)
  ) || Object.keys(data[0]).find(key => 
    key !== xAxisColumn && 
    key !== 'TotalBalance' && 
    key !== 'Balance' &&
    typeof data[0][key] === 'string'
  );
  
  const valueColumn = config?.yAxis?.primary?.columns?.[0] || 'TotalBalance';

  if (!groupColumn) {
    console.warn('StackedBarChart - No grouping column found, falling back to simple bar chart');
    // Fall back to simple bar chart if no grouping column found
    const simpleColumns = Object.keys(data[0]).filter(key => 
      key !== xAxisColumn && typeof data[0][key] === 'number'
    );
    return (
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey={xAxisColumn}
            angle={-45}
            textAnchor="end"
            height={80}
            fontSize={12}
          />
          <YAxis 
            fontSize={12}
            tickFormatter={(value) => {
              if (config?.yAxis?.primary?.format === 'currency') {
                return `$${(value / 1000000).toFixed(0)}M`;
              }
              return value.toLocaleString();
            }}
          />
          <Tooltip 
            content={<CustomTooltip 
              config={config} 
              xAxisLabel={config?.xAxis?.label || 'Category'} 
              currencyFormat={config?.yAxis?.primary?.format === 'currency'}
            />} 
          />
          <Legend />
          {simpleColumns.map((column, index) => (
            <Bar 
              key={column}
              dataKey={column} 
              stackId="stack"
              fill={colors[index % colors.length]}
              name={column ? column.replace(/_/g, ' ') : `Series ${index + 1}`}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    );
  }
  
  // Get unique groups
  const uniqueGroups = Array.from(new Set(data.map(d => d[groupColumn])));
  
  // Pivot data to create separate columns for each group
  const pivotedData = data.reduce((acc: any[], item: any) => {
    const existingItem = acc.find((d: any) => d[xAxisColumn] === item[xAxisColumn]);
    if (existingItem) {
      existingItem[item[groupColumn]] = item[valueColumn];
    } else {
      const newItem: any = { [xAxisColumn]: item[xAxisColumn] };
      newItem[item[groupColumn]] = item[valueColumn];
      acc.push(newItem);
    }
    return acc;
  }, []);

  // Use the pivoted data and unique groups as stack columns
  const stackColumns = uniqueGroups;


  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart data={pivotedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey={xAxisColumn}
          angle={-45}
          textAnchor="end"
          height={80}
          fontSize={12}
        />
        <YAxis 
          fontSize={12}
          tickFormatter={(value) => {
            if (config?.yAxis?.primary?.format === 'currency') {
              return `$${(value / 1000000).toFixed(0)}M`;
            }
            return value.toLocaleString();
          }}
        />
        <Tooltip 
          content={<CustomTooltip 
            config={config} 
            xAxisLabel={config?.xAxis?.label || 'Category'} 
            currencyFormat={config?.yAxis?.primary?.format === 'currency'}
          />} 
        />
        <Legend />
        {stackColumns.map((column, index) => (
          <Bar 
            key={column}
            dataKey={column} 
            stackId="stack"
            fill={colors[index % colors.length]}
            name={column ? column.replace(/_/g, ' ') : `Series ${index + 1}`}
          />
        ))}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

export default StackedBarChart; 